/**
 * Saber cuándo terminó una descarga que hace el navegador.
 *
 * Un archivo se baja siguiendo un enlace —es lo único que no falla en
 * silencio—, pero una navegación de descarga no dispara ningún evento en la
 * página: no hay forma directa de saber si el servidor contestó.
 *
 * La vuelta es un acuse por cookie: el front manda un token en la URL y el
 * servidor lo devuelve como cookie junto con el archivo. Cuando la cookie
 * aparece, la respuesta llegó. Es lo que permite cerrar el cartel de espera en
 * el momento justo en vez de calcular una espera inventada.
 */

/** Token de un solo uso para el acuse. No identifica a nadie. */
export function tokenDeDescarga(): string {
  return (
    Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
  ).replace(/[^a-z0-9]/g, '');
}

function hayAcuse(token: string): boolean {
  return document.cookie.split('; ').includes(`descarga=${token}`);
}

function borrarAcuse(): void {
  document.cookie = 'descarga=; path=/; max-age=0';
}

export interface EsperaDeDescarga {
  /** Deja de esperar. No aborta la descarga: eso lo maneja el navegador. */
  readonly cancelar: () => void;
}

/**
 * Espera el acuse del servidor y avisa.
 *
 * `motivo` dice por qué terminó: `'acuse'` cuando el archivo llegó, `'plazo'`
 * cuando se agotó la espera. Lo segundo pasa si el navegador no guarda la
 * cookie, y por eso la espera termina igual: un cartel que no se cierra nunca
 * es peor que uno que se cierra sin certeza.
 */
export function esperarDescarga(
  token: string,
  alTerminar: (motivo: 'acuse' | 'plazo') => void,
  plazoMs = 90_000,
): EsperaDeDescarga {
  borrarAcuse();
  const inicio = Date.now();

  const reloj = setInterval(() => {
    if (hayAcuse(token)) {
      terminar('acuse');
    } else if (Date.now() - inicio >= plazoMs) {
      terminar('plazo');
    }
  }, 250);

  function terminar(motivo: 'acuse' | 'plazo'): void {
    clearInterval(reloj);
    borrarAcuse();
    alTerminar(motivo);
  }

  return {
    cancelar: () => {
      clearInterval(reloj);
      borrarAcuse();
    },
  };
}
